import React, { useState, useEffect, useCallback } from "react";
import ListView from "../../../../components/common/ListView";
import {
  IconSearch,
  IconPlus,
  IconTrash,
  IconPencil,
  IconRefresh,
  IconX,
  IconEye,
  IconChevronLeft,
  IconChevronRight,
  IconDotsVertical,
  IconInfoCircle,
} from "@tabler/icons-react";
import RoleToggle from "../../../../components/RoleToggle";
import InputField from "../../../../components/common/InputField";
import TextareaField from "../../../../components/common/TextareaField";
import {
  fetchHrFines,
  createHrFine,
  updateHrFine,
  deleteHrFine,
  fetchHrCompany,
  fetchHrBranches,
  fetchHrWarningSettings,
  saveHrWarningSettings,
  fetchHrNoticePeriodSettings,
  saveHrNoticePeriodSettings,
  fetchHrExpenseRequests,
  fetchHrExpenseRequestById,
  updateHrExpenseRequestStatus,
  fetchHrRelegations,
  createHrRelegation,
  fetchHrDesignations,
  fetchHrEmployees,
} from "../../../../apis/hrApi";

import toast from "react-hot-toast";

interface TabProps {
  companyId: string;
  branchId: string;
  isLoadingMeta: boolean;
}

function WarningTab({ companyId, branchId, isLoadingMeta }: TabProps) {
  const [warningCount, setWarningCount] = useState(10);
  const [originalCount, setOriginalCount] = useState(10);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const loadWarning = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await fetchHrWarningSettings();
      if (res.success && res.data) {
        const data = Array.isArray(res.data) ? res.data[0] : res.data;
        if (data) {
          const count = typeof data.warningCount === "number"
            ? data.warningCount
            : parseInt(data.warningCount, 10) || 10;
          setWarningCount(count);
          setOriginalCount(count);
        }
      }
    } catch (err) {
      console.error("Failed to load warning settings:", err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadWarning();
  }, [loadWarning]);

  const handleSave = async () => {
    setIsSubmitting(true);
    try {
      const res = await saveHrWarningSettings({
        warningCount: Number(warningCount),
      });
      if (res.success) {
        toast.success("Warning settings saved successfully!");
        setOriginalCount(warningCount);
      } else {
        toast.error(res.message || "Failed to save warning settings");
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to save warning settings");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setWarningCount(originalCount);
    toast.success("Changes discarded.");
  };

  return (
    <div className="space-y-4 border border-[#d2d2d2] rounded-[20px] p-4 bg-gradient-to-r from-[#FFFFFD] to-[#FDF8E5] mb-4 mt-2 max-w-4xl mx-auto animate-in fade-in duration-300">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-medium text-[#212121]">Warning Setting</h2>
        {(isLoading || isLoadingMeta) && (
          <span className="text-xs text-gray-400 font-medium animate-pulse">Loading...</span>
        )}
      </div>

      <div className="bg-[#FFFFFF] p-6 rounded-[20px] space-y-6 border border-[#D2D2D2] shadow-sm">
        <div className="mb-8 max-w-md">
          <label className="block text-sm font-medium text-[#212121] mb-2">
            Warning Count
          </label>
          <input
            type="number"
            value={warningCount}
            onChange={(e) => setWarningCount(parseInt(e.target.value, 10) || 0)}
            className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl outline-none focus:ring-1 focus:ring-[#FBD037] text-[#212121] text-sm"
          />
        </div>

        <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
          <button
            onClick={handleSave}
            disabled={isSubmitting || isLoading || isLoadingMeta}
            className="bg-[#FBD037] text-[#212121] text-xs font-semibold px-5 py-2.5 rounded-xl hover:bg-[#e5bc32] transition duration-150 cursor-pointer active:scale-95 border-none"
          >
            {isSubmitting ? "Saving..." : "Save Settings"}
          </button>
          <button
            type="button"
            onClick={handleCancel}
            className="border border-[#D2D2D2] text-[#212121] text-xs font-semibold px-5 py-2.5 rounded-xl hover:bg-gray-50 bg-white transition cursor-pointer"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

function NoticePeriodTab({ companyId, branchId, isLoadingMeta }: TabProps) {
  const [noticePeriod, setNoticePeriod] = useState("3");
  const [noticeTenure, setNoticeTenure] = useState("DAYS");
  const [originalPeriod, setOriginalPeriod] = useState("3");
  const [originalTenure, setOriginalTenure] = useState("DAYS");
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const loadNotice = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await fetchHrNoticePeriodSettings();
      if (res.success && res.data) {
        const data = Array.isArray(res.data) ? res.data[0] : res.data;
        if (data) {
          setNoticePeriod(data.noticePeriod?.toString() || "3");
          const tenure = (data.noticeTenure || "DAYS").toUpperCase();
          setNoticeTenure(tenure);
          setOriginalPeriod(data.noticePeriod?.toString() || "3");
          setOriginalTenure(tenure);
        }
      }
    } catch (err) {
      console.error("Failed to load notice period settings:", err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadNotice();
  }, [loadNotice]);

  const handleSave = async () => {
    setIsSubmitting(true);
    try {
      const res = await saveHrNoticePeriodSettings({
        noticePeriod: Number(noticePeriod),
        noticeTenure: noticeTenure.toUpperCase(),
      });
      if (res.success) {
        toast.success("Notice period settings saved successfully!");
        setOriginalPeriod(noticePeriod);
        setOriginalTenure(noticeTenure);
      } else {
        toast.error(res.message || "Failed to save notice period settings");
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to save notice period settings");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setNoticePeriod(originalPeriod);
    setNoticeTenure(originalTenure);
    toast.success("Changes discarded.");
  };

  return (
    <div className="space-y-4 border border-[#d2d2d2] rounded-[20px] p-4 bg-gradient-to-r from-[#FFFFFD] to-[#FDF8E5] mb-4 mt-2 max-w-4xl mx-auto animate-in fade-in duration-300">
      <div className="bg-[#FFFFFF] p-6 rounded-[20px] space-y-6 border border-[#D2D2D2] shadow-sm">
        <div className="flex justify-between items-center py-2 border-b border-slate-100">
          <h2 className="text-xl font-bold text-[#212121] tracking-tight">Notice Period Setting</h2>
          {(isLoading || isLoadingMeta) && (
            <span className="text-xs text-gray-400 font-medium animate-pulse">Loading...</span>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div className="space-y-2">
            <label className="block text-xs font-semibold text-[#1E293B]">
              Notice Period
            </label>
            <input
              type="text"
              placeholder="Ex. 3"
              value={noticePeriod}
              onChange={(e) => setNoticePeriod(e.target.value)}
              className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl outline-none focus:ring-1 focus:ring-[#FBD037] text-sm text-[#212121]"
            />
          </div>

          <div className="space-y-2">
            <label className="block text-xs font-semibold text-[#1E293B]">
              Notice Tenure
            </label>
            <div className="relative">
              <select
                value={noticeTenure}
                onChange={(e) => setNoticeTenure(e.target.value)}
                className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl outline-none focus:ring-1 focus:ring-[#FBD037] text-sm text-[#212121] appearance-none cursor-pointer"
              >
                <option value="DAYS">Days</option>
                <option value="WEEKS">Weeks</option>
                <option value="MONTHS">Months</option>
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-slate-500">
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-4 border-t border-slate-100">
          <button
            type="button"
            onClick={handleSave}
            disabled={isSubmitting || isLoading || isLoadingMeta}
            className="bg-black hover:bg-slate-900 text-white text-xs font-bold px-6 py-2.5 rounded-xl transition duration-150 active:scale-95 shadow-sm cursor-pointer border-none"
          >
            {isSubmitting ? "Saving..." : "Save Settings"}
          </button>
          <button
            type="button"
            onClick={handleCancel}
            className="border border-[#D2D2D2] text-[#212121] text-xs font-semibold px-5 py-2.5 rounded-xl hover:bg-gray-50 bg-white transition cursor-pointer"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}


function FineTab({ companyId, branchId, isLoadingMeta }: { companyId: string; branchId: string; isLoadingMeta: boolean }) {
  const [finesData, setFinesData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [showSearch, setShowSearch] = useState(false);

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [limit] = useState(10);

  // Sidebar Panel State
  const [sidebarMode, setSidebarMode] = useState<"ADD" | "EDIT" | "DELETE" | null>(null);
  const [selectedItem, setSelectedItem] = useState<any | null>(null);

  // Form fields
  const [name, setName] = useState("");
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [status, setStatus] = useState<"ACTIVE" | "INACTIVE">("ACTIVE");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const loadFines = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await fetchHrFines(currentPage, limit, searchQuery);
      if (res.success) {
        let list: any[] = [];
        if (Array.isArray(res.data)) {
          list = res.data;
        } else if (res.data && typeof res.data === "object") {
          const possible = res.data.fineCategories || res.data.fineCategory || res.data.list || res.data.data;
          if (Array.isArray(possible)) {
            list = possible;
          } else {
            const found = Object.values(res.data).find(Array.isArray);
            if (found) list = found as any[];
          }
        }
        setFinesData(list);
        const total = res.data?.total || res.data?.totalCount || list.length;
        setTotalRecords(total);
        setTotalPages(res.data?.totalPages || Math.ceil(total / limit) || 1);
      } else {
        setError("Failed to load fine categories");
      }
    } catch (err) {
      console.error(err);
      setError("Failed to load fine categories");
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, limit, searchQuery]);

  useEffect(() => {
    loadFines();
  }, [loadFines]);

  // handle switch toggle directly
  const handleToggleStatus = async (item: any) => {
    try {
      const newStatus = item.status === "ACTIVE" ? "INACTIVE" : "ACTIVE";
      const payload = {
        name: item.name,
        amount: item.amount,
        description: item.description,
        status: newStatus,
      };
      const res = await updateHrFine(item.id || item._id, payload);
      if (res.success) {
        toast.success(`Status updated to ${newStatus}`);
        loadFines();
      } else {
        toast.error(res.message || "Failed to update status");
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to update status");
    }
  };

  // Open sidebar forms
  const handleOpenAdd = () => {
    setName("");
    setAmount("");
    setDescription("");
    setStatus("ACTIVE");
    setSelectedItem(null);
    setSidebarMode("ADD");
  };

  const handleOpenEdit = (item: any) => {
    setName(item.name || "");
    setAmount(item.amount?.toString() || "");
    setDescription(item.description || "");
    setStatus(item.status || "ACTIVE");
    setSelectedItem(item);
    setSidebarMode("EDIT");
  };

  const handleOpenDelete = (item: any) => {
    setSelectedItem(item);
    setSidebarMode("DELETE");
  };

  // Submit Operations
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error("Please enter a Fine Name.");
      return;
    }
    if (!amount.trim()) {
      toast.error("Please enter a Fine Amount.");
      return;
    }
    if (!description.trim()) {
      toast.error("Please enter a Fine Description.");
      return;
    }

    setIsSubmitting(true);
    const payload: any = {
      name: name.trim(),
      amount: parseFloat(amount),
      description: description.trim(),
    };

    if (sidebarMode === "EDIT") {
      payload.status = status;
    }

    try {
      let res;
      if (sidebarMode === "EDIT" && selectedItem) {
        res = await updateHrFine(selectedItem.id || selectedItem._id, payload);
      } else {
        res = await createHrFine(payload);
      }

      if (res.success) {
        toast.success(
          sidebarMode === "EDIT"
            ? "Fine Category updated successfully"
            : "Fine Category created successfully"
        );
        loadFines();
        setSidebarMode(null);
        setSelectedItem(null);
      } else {
        toast.error(res.message || "Failed to save fine category");
      }
    } catch (err: any) {
      console.error(err);
      toast.error(err?.message || "An error occurred while saving");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!selectedItem) return;
    setIsSubmitting(true);
    try {
      const id = selectedItem.id || selectedItem._id;
      const res = await deleteHrFine(id);
      if (res.success) {
        toast.success("Fine Category deleted successfully");
        loadFines();
        setSidebarMode(null);
        setSelectedItem(null);
      } else {
        toast.error(res.message || "Failed to delete");
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete fine category");
    } finally {
      setIsSubmitting(false);
    }
  };

  // ListView Columns Definition
  const columns = [
    {
      label: "FINE NAME",
      render: (s: any) => <span className="font-semibold text-gray-800 text-sm">{s.name}</span>,
    },
    {
      label: "AMOUNT",
      render: (s: any) => <span className="font-semibold text-[#212121] text-sm">{s.amount ?? "-"}</span>,
    },
    {
      label: "DESCRIPTION",
      render: (s: any) => <span className="text-gray-500 text-sm">{s.description || "-"}</span>,
    },
    {
      label: "STATUS",
      render: (s: any) => (
        <button
          type="button"
          onClick={() => handleToggleStatus(s)}
          className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${s.status === "ACTIVE" ? "bg-black" : "bg-gray-200"
            }`}
        >
          <span
            className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${s.status === "ACTIVE" ? "translate-x-5" : "translate-x-0"
              }`}
          />
        </button>
      ),
    },
    {
      label: "ACTIONS",
      render: (s: any) => (
        <div className="flex justify-center items-center gap-2">
          <button
            onClick={() => handleOpenEdit(s)}
            className="w-8 h-8 rounded-full border border-gray-200 flex items-center justify-center hover:bg-gray-50 transition text-slate-500 cursor-pointer bg-white"
            title="Edit"
          >
            <IconPencil size={15} stroke={1.5} />
          </button>
          <button
            onClick={() => handleOpenDelete(s)}
            className="w-8 h-8 rounded-full border border-red-100 flex items-center justify-center hover:bg-red-50 transition text-red-500 cursor-pointer bg-white"
            title="Delete"
          >
            <IconTrash size={15} stroke={1.5} />
          </button>
        </div>
      ),
    },
  ];

  const isSidebarOpen = sidebarMode !== null;

  // Filter columns dynamically to fit when the right sidebar is open
  const activeColumns = isSidebarOpen
    ? columns.filter((_, idx) => [0, 1, 3, 4].includes(idx)) // Show FINE NAME, AMOUNT, STATUS, ACTIONS
    : columns;

  const sidebarContent = sidebarMode ? (
    <div className="flex flex-col bg-white rounded-3xl border border-gray-200 shadow-sm overflow-hidden animate-in slide-in-from-right duration-200">
      <div className="flex items-center justify-between px-5 py-3.5 shrink-0 border-b border-gray-100 bg-white">
        <h2 className="text-lg font-bold text-[#212121]">
          {sidebarMode === "ADD"
            ? "Add Fine Category"
            : sidebarMode === "EDIT"
              ? "Edit Fine Category"
              : "Delete Fine Category"}
        </h2>
        <button
          type="button"
          onClick={() => {
            setSidebarMode(null);
            setSelectedItem(null);
          }}
          className="p-1.5 rounded-lg border border-[#D2D2D2] hover:bg-gray-100 transition-colors text-slate-700 cursor-pointer"
          title="Close"
        >
          <IconX size={18} />
        </button>
      </div>

      {sidebarMode === "DELETE" ? (
        <div className="p-6 bg-white space-y-6">
          <p className="text-sm text-gray-600 font-medium">
            Are you sure you want to delete this Fine Category?
          </p>
          {selectedItem && (
            <div className="p-3 bg-slate-50 border border-slate-100 rounded-xl flex items-center justify-between">
              <span className="text-xs font-semibold text-slate-500">NAME</span>
              <span className="text-sm font-semibold text-gray-800">{selectedItem.name}</span>
            </div>
          )}
          <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
            <button
              type="button"
              onClick={() => {
                setSidebarMode(null);
                setSelectedItem(null);
              }}
              className="px-4 py-2 border border-gray-200 hover:bg-gray-50 text-xs font-semibold rounded-xl text-slate-600 transition cursor-pointer bg-white"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirmDelete}
              disabled={isSubmitting}
              className="px-4 py-2 bg-[#FF0000] hover:bg-red-700 text-white text-xs font-semibold rounded-xl transition cursor-pointer border-none flex items-center justify-center min-w-max"
            >
              {isSubmitting ? "Deleting..." : "Delete"}
            </button>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="bg-white p-6 max-h-[calc(100vh-160px)] overflow-y-auto space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-1 gap-4">
            <InputField
              label="Fine Name *"
              placeholder="Ex. Late Coming"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
            <InputField
              label="Fine Amount *"
              placeholder="0"
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              required
            />
          </div>

          <div className="flex items-center justify-between gap-4 py-2 border-t border-b border-gray-50">
            <div className="space-y-1">
              <span className="text-[#1E293B] text-xs font-semibold block">Status</span>
              <span className="text-[11px] text-gray-400">Determine if this category is active</span>
            </div>
            <button
              type="button"
              onClick={() => setStatus(status === "ACTIVE" ? "INACTIVE" : "ACTIVE")}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${status === "ACTIVE" ? "bg-black" : "bg-gray-200"
                }`}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${status === "ACTIVE" ? "translate-x-5" : "translate-x-0"
                  }`}
              />
            </button>
          </div>

          <TextareaField
            label="Fine Description *"
            placeholder="Ex. Fine for arriving late to office beyond 15 minutes"
            value={description}
            name="description"
            onChange={(e) => setDescription(e.target.value)}
            rows={4}
          />

          <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
            <button
              type="button"
              onClick={() => {
                setSidebarMode(null);
                setSelectedItem(null);
              }}
              className="px-4 py-2 border border-gray-200 hover:bg-gray-50 text-xs font-semibold rounded-xl text-slate-600 transition cursor-pointer bg-white"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 bg-[#FBD037] text-[#212121] hover:bg-[#e2bd30] text-xs font-bold rounded-xl transition cursor-pointer border-none flex items-center justify-center min-w-max shadow-sm hover:shadow-md duration-150 hover:-translate-y-0.5 active:translate-y-0"
            >
              {isSubmitting ? (
                <svg
                  className="animate-spin -ml-1 mr-2 h-4 w-4 text-[#212121]"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                </svg>
              ) : null}
              <span>{sidebarMode === "EDIT" ? "Update Fine" : "Create Fine"}</span>
            </button>
          </div>
        </form>
      )}
    </div>
  ) : null;

  return (
    <div className="space-y-4 border border-[#d2d2d2] rounded-[20px] p-4 bg-gradient-to-r from-[#FFFFFD] to-[#FDF8E5] mb-4 mt-2 max-w-5xl mx-auto animate-in fade-in duration-300">
      <div className="grid grid-cols-12 gap-6 items-start w-full">
        <div className={isSidebarOpen ? "col-span-12 xl:col-span-7 transition-all duration-300" : "col-span-12"}>
          <div className="bg-white rounded-3xl border border-gray-200/50 shadow-sm overflow-hidden flex flex-col justify-start">
            <div className="px-6 py-2.5 flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-100">
              <h2 className="text-lg font-medium text-[#212121]">Fine Setting</h2>
              <div className="flex items-center flex-wrap gap-2.5 justify-start sm:justify-end">
                {/* Search input slider */}
                <div className="relative flex items-center group">
                  <div
                    onClick={() => {
                      if (!showSearch) setShowSearch(true);
                    }}
                    className={`flex items-center transition-all duration-300 ease-in-out border border-slate-200 rounded-lg bg-white shadow-sm h-8 ${showSearch
                      ? "w-48 px-2 cursor-default"
                      : "w-8 justify-center cursor-pointer hover:bg-slate-50 hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 active:scale-95"
                      }`}
                  >
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowSearch(!showSearch);
                      }}
                      className="flex items-center justify-center text-slate-700 focus:outline-none flex-shrink-0"
                    >
                      <IconSearch size={17} stroke={2.5} />
                    </button>
                    {showSearch && (
                      <>
                        <input
                          type="text"
                          placeholder="Search"
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          autoFocus
                          className="flex-1 ml-2 bg-transparent text-xs text-slate-800 placeholder-slate-400 outline-none border-none animate-in fade-in duration-200"
                        />
                        {searchQuery && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSearchQuery("");
                            }}
                            className="flex items-center justify-center p-0.5 ml-1 text-slate-400 hover:text-slate-600 focus:outline-none flex-shrink-0"
                          >
                            <IconX size={14} stroke={2.5} />
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {/* Refresh button */}
                <button
                  onClick={loadFines}
                  className="w-8 h-8 flex items-center justify-center bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 rounded-lg transition-all duration-150 shadow-sm hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 active:scale-95 cursor-pointer"
                  title="Refresh"
                >
                  <IconRefresh size={17} stroke={2.5} />
                </button>

                {/* Add Fine Category button */}
                <button
                  onClick={handleOpenAdd}
                  className="flex items-center justify-center w-8 h-8 rounded-lg bg-[#FBD037] hover:bg-[#e2bd30] border border-[#FBD037] text-[#212121] active:scale-95 transition shadow-sm hover:shadow-md cursor-pointer"
                  title="Add Fine Category"
                >
                  <IconPlus stroke={2.5} size={17} />
                </button>
              </div>
            </div>

            <div className="pb-2">
              {isLoading ? (
                <div className="p-8 flex justify-center items-center">
                  <svg
                    className="animate-spin h-6 w-6 text-[#FBD037]"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                  </svg>
                </div>
              ) : error ? (
                <div className="p-8 text-center text-red-500 font-semibold">{error}</div>
              ) : finesData.length === 0 ? (
                <div className="p-8 text-center text-gray-400 font-medium">No Fine Categories Found</div>
              ) : (
                <>
                  <ListView
                    mainDivClassName="bg-transparent"
                    tdClassName="rounded-r-none rounded-l-none"
                    trClassName="hover:bg-[#FBD037]/20 transition-colors border-b border-gray-100"
                    mailDivClassName="p-0 bg-transparent"
                    thClassName="border-t"
                    columns={activeColumns}
                    data={finesData}
                  />
                  <div className="flex justify-end px-6 py-4 text-sm text-gray-500 items-center gap-2">
                    <span>
                      Record {(currentPage - 1) * limit + 1}-{Math.min(currentPage * limit, totalRecords)} of{" "}
                      {totalRecords}
                    </span>
                    <span className="mx-2">{limit}</span>
                    <button
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="opacity-50 disabled:opacity-20 cursor-pointer"
                    >
                      &lt;
                    </button>
                    <span>
                      {currentPage}/{totalPages}
                    </span>
                    <button
                      onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className="opacity-50 disabled:opacity-20 cursor-pointer"
                    >
                      &gt;
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Desktop Sidebar (static column inside grid) */}
        {isSidebarOpen && (
          <div className="hidden xl:block xl:col-span-5 space-y-4 mt-2">
            {sidebarContent}
          </div>
        )}
      </div>

      {/* Mobile/Tablet Sidebar (fixed overlay outside grid) */}
      {isSidebarOpen && (
        <div className="xl:hidden">
          <div
            className="fixed inset-0 bg-black/40 z-30"
            onClick={() => {
              setSidebarMode(null);
              setSelectedItem(null);
            }}
          />
          <div className="fixed inset-y-0 right-0 z-[35] w-full sm:w-[500px] p-4 pt-[76px] sm:pt-[96px] shadow-2xl bg-[#F7F6F2] overflow-y-auto flex flex-col justify-start">
            {sidebarContent}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Relegation Tab ────────────────────────────────────────────────────────
function RelegationTab() {
  // ── Table state ─────────────────────────────────────────────────────────
  const [data, setData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [limit] = useState(10);

  // ── Add form state ───────────────────────────────────────────────────────
  const [showForm, setShowForm] = useState(false);
  const [employees, setEmployees] = useState<any[]>([]);
  const [designations, setDesignations] = useState<any[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form fields
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [oldDesignation, setOldDesignation] = useState("");
  const [newDesignation, setNewDesignation] = useState("");
  const [effectiveDate, setEffectiveDate] = useState("");
  const [oldCtc, setOldCtc] = useState("");
  const [newCtc, setNewCtc] = useState("");
  const [remarks, setRemarks] = useState("");

  // Auto-calculated values
  const decrementAmount = oldCtc && newCtc
    ? Math.max(0, parseFloat(oldCtc) - parseFloat(newCtc)).toFixed(2)
    : "";
  const decrementPct = oldCtc && newCtc && parseFloat(oldCtc) > 0
    ? (((parseFloat(oldCtc) - parseFloat(newCtc)) / parseFloat(oldCtc)) * 100).toFixed(2)
    : "";

  // ── Load data ────────────────────────────────────────────────────────────
  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await fetchHrRelegations(currentPage, limit, search);
      if (res.success) {
        const raw = res.data?.relegations ?? res.data?.data ?? res.data ?? [];
        const list = Array.isArray(raw) ? raw : [];
        setData(list);
        const total = res.data?.total ?? res.data?.totalCount ?? list.length;
        setTotalRecords(total);
        setTotalPages(res.data?.totalPages ?? (Math.ceil(total / limit) || 1));
      }
    } catch (err) {
      console.error("Failed to load relegations:", err);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, limit, search]);

  useEffect(() => { loadData(); }, [loadData]);

  // Load employees and designations when form opens
  useEffect(() => {
    if (!showForm) return;
    const load = async () => {
      try {
        const [empRes, desRes] = await Promise.all([
          fetchHrEmployees(1, 200),
          fetchHrDesignations(1, 200),
        ]);
        if (empRes.success) {
          const empRaw = empRes.data?.employees ?? (empRes.data as any) ?? [];
          setEmployees(Array.isArray(empRaw) ? empRaw : []);
        }
        if (desRes.success) {
          const desRaw = desRes.data?.designations ?? (desRes.data as any)?.data ?? (desRes.data as any) ?? [];
          setDesignations(Array.isArray(desRaw) ? desRaw : []);
        }
      } catch (err) {
        console.error("Failed to load form data:", err);
      }
    };
    load();
  }, [showForm]);

  // Auto-fill old designation when employee is selected
  const handleEmployeeChange = (empId: string) => {
    setSelectedEmployee(empId);
    const emp = employees.find((e: any) => (e.id || e._id) === empId);
    const desigId = emp?.designationId || emp?.designation?.id || emp?.designation?._id || "";
    const desigName = emp?.designationName || emp?.designation?.designationName || emp?.designation?.name || "";
    setOldDesignation(desigId || desigName);
  };

  // Reset form
  const resetForm = () => {
    setSelectedEmployee(""); setOldDesignation(""); setNewDesignation("");
    setEffectiveDate(""); setOldCtc(""); setNewCtc(""); setRemarks("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedEmployee || !effectiveDate || !remarks.trim()) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setIsSubmitting(true);
    try {
      const payload: any = {
        employeeId: selectedEmployee,
        oldDesignationId: oldDesignation,
        newDesignationId: newDesignation,
        effectiveDate,
        oldCtc: oldCtc ? parseFloat(oldCtc) : undefined,
        newCtc: newCtc ? parseFloat(newCtc) : undefined,
        decrementAmount: decrementAmount ? parseFloat(decrementAmount) : undefined,
        decrementPercentage: decrementPct ? parseFloat(decrementPct) : undefined,
        remarks: remarks.trim(),
      };
      const res = await createHrRelegation(payload);
      if (res.success) {
        toast.success("Relegation added successfully!");
        setShowForm(false);
        resetForm();
        loadData();
      } else {
        toast.error(res.message || "Failed to add relegation");
      }
    } catch (err: any) {
      toast.error(err.message || "Failed to add relegation");
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDate = (d: string) => {
    if (!d) return "-";
    const dt = new Date(d);
    if (isNaN(dt.getTime())) return d;
    return dt.toLocaleDateString("en-IN", { day: "2-digit", month: "2-digit", year: "numeric" }).replace(/\//g, "-");
  };

  const formatDateTime = (d: string) => {
    if (!d) return "-";
    const dt = new Date(d);
    if (isNaN(dt.getTime())) return d;
    return (
      dt.toLocaleDateString("en-IN", { day: "2-digit", month: "2-digit", year: "numeric" }).replace(/\//g, "-") +
      "\n" +
      dt.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true })
    );
  };

  const colClass = "px-4 py-3 text-[11px] font-bold uppercase tracking-wider text-gray-500 whitespace-nowrap";
  const cellClass = "px-4 py-3 text-[13px] text-[#212121] border-b border-gray-100 whitespace-nowrap";
  const inputCls = "w-full px-3.5 py-2.5 text-sm rounded-xl border border-[#D2D2D2] bg-white outline-none focus:border-[#FBD037] focus:ring-1 focus:ring-[#FBD037]/40 transition text-[#212121] placeholder-gray-400";
  const labelCls = "block text-xs font-bold text-[#212121] mb-1.5";

  return (
    <div className="space-y-4 animate-in fade-in duration-300">
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-xl px-3 py-2 shadow-sm w-64">
          <IconSearch size={15} className="text-gray-400 shrink-0" />
          <input
            type="text" placeholder="Search..." value={search}
            onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
            className="flex-1 text-sm bg-transparent outline-none text-[#212121] placeholder-gray-400"
          />
          {search && <button onClick={() => { setSearch(""); setCurrentPage(1); }} className="text-gray-400 hover:text-gray-600 cursor-pointer"><IconX size={14} /></button>}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-400">{limit} entries</span>
          <button
            onClick={() => { setShowForm(true); resetForm(); }}
            className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-[#FBD037] hover:bg-[#e5bc32] text-[#212121] text-xs font-bold shadow-sm transition cursor-pointer"
          >
            <IconPlus size={14} /> Add Relegation
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50/80 border-b border-gray-100">
              <tr>
                <th className={colClass}></th>
                <th className={colClass}>SR NO.</th>
                <th className={colClass}>Employee</th>
                <th className={colClass}>Old Designation</th>
                <th className={colClass}>New Designation</th>
                <th className={colClass}>Old CTC</th>
                <th className={colClass}>New CTC</th>
                <th className={colClass}>Effective Date</th>
                <th className={colClass}>Status</th>
                <th className={colClass}>Created At</th>
                <th className={colClass}>Updated At</th>
                <th className={colClass}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr><td colSpan={12} className="text-center py-12 text-gray-400 text-sm">Loading...</td></tr>
              ) : data.length === 0 ? (
                <tr><td colSpan={12} className="text-center py-12 text-gray-400 text-sm">No relegation records found.</td></tr>
              ) : data.map((item: any, idx: number) => {
                const name = item.employeeName || item.employee?.name || "-";
                const initials = name !== "-" ? name.split(" ").map((n: string) => n[0]).join("").slice(0, 2).toUpperCase() : "?";
                const dept = item.department || item.employee?.department || "";
                const srNo = (currentPage - 1) * limit + idx + 1;
                const itemId = item.id || item._id;
                const [createdDate, createdTime] = formatDateTime(item.createdAt).split("\n");
                const [updatedDate, updatedTime] = formatDateTime(item.updatedAt).split("\n");
                return (
                  <tr key={itemId} className="hover:bg-gray-50/60 transition-colors">
                    <td className={cellClass}>
                      <input type="checkbox" className="w-3.5 h-3.5 rounded border-gray-300 accent-[#FBD037] cursor-pointer" />
                    </td>
                    <td className={`${cellClass} font-semibold text-gray-400`}>{srNo}</td>
                    <td className={cellClass}>
                      <div className="flex items-center gap-2.5">
                        <span className="w-8 h-8 rounded-full bg-[#FBD037]/30 border border-[#FBD037]/50 flex items-center justify-center text-xs font-bold text-[#212121] shrink-0">{initials}</span>
                        <div>
                          <div className="font-semibold text-[#212121] text-[13px]">{name}</div>
                          {dept && <div className="text-[11px] text-gray-400">{dept}</div>}
                        </div>
                      </div>
                    </td>
                    <td className={cellClass}>{item.oldDesignationName || item.oldDesignation?.name || "-"}</td>
                    <td className={cellClass}>{item.newDesignationName || item.newDesignation?.name || "-"}</td>
                    <td className={cellClass}>{item.oldCtc != null ? `₹${Number(item.oldCtc).toLocaleString("en-IN")}` : "-"}</td>
                    <td className={cellClass}>{item.newCtc != null ? `₹${Number(item.newCtc).toLocaleString("en-IN")}` : "-"}</td>
                    <td className={cellClass}>{formatDate(item.effectiveDate)}</td>
                    <td className={cellClass}><StatusBadge status={item.status || "Pending"} /></td>
                    <td className={cellClass}>
                      <div className="text-[12px]">{createdDate}</div>
                      <div className="text-[11px] text-gray-400">{createdTime}</div>
                    </td>
                    <td className={cellClass}>
                      <div className="text-[12px]">{updatedDate}</div>
                      <div className="text-[11px] text-gray-400">{updatedTime}</div>
                    </td>
                    <td className={cellClass}>
                      <button className="p-1.5 rounded-lg hover:bg-gray-100 transition cursor-pointer">
                        <IconDotsVertical size={16} className="text-gray-400" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 text-sm text-gray-500">
            <span>Record {(currentPage - 1) * limit + 1}–{Math.min(currentPage * limit, totalRecords)} of {totalRecords}</span>
            <div className="flex items-center gap-1">
              <button disabled={currentPage <= 1} onClick={() => setCurrentPage(p => p - 1)}
                className="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-40 transition cursor-pointer">
                <IconChevronLeft size={15} />
              </button>
              {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => i + 1).map(pg => (
                <button key={pg} onClick={() => setCurrentPage(pg)}
                  className={`w-8 h-8 rounded-lg text-xs font-semibold transition cursor-pointer ${
                    pg === currentPage ? "bg-[#FBD037] text-[#212121] shadow-sm" : "border border-gray-200 hover:bg-gray-50 text-gray-500"
                  }`}>{pg}</button>
              ))}
              <button disabled={currentPage >= totalPages} onClick={() => setCurrentPage(p => p + 1)}
                className="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-40 transition cursor-pointer">
                <IconChevronRight size={15} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── Add New Relegation Modal ─────────────────────────────────────── */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl mx-4 overflow-hidden animate-in zoom-in-95 duration-200">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h2 className="text-base font-bold text-[#212121]">Add New Relegation</h2>
              <button onClick={() => { setShowForm(false); resetForm(); }}
                className="p-1.5 rounded-lg hover:bg-gray-100 transition cursor-pointer">
                <IconX size={18} />
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
              {/* Row 1: Employee + Old Designation */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Employee <span className="text-red-500">*</span></label>
                  <select value={selectedEmployee} onChange={(e) => handleEmployeeChange(e.target.value)}
                    required className={inputCls}>
                    <option value="">Select Employee</option>
                    {employees.map((e: any) => (
                      <option key={e.id || e._id} value={e.id || e._id}>
                        {e.firstName || e.name || ""} {e.lastName || ""}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Old Designation</label>
                  <input readOnly value={
                    designations.find((d: any) => (d.id || d._id) === oldDesignation)?.designationName ||
                    designations.find((d: any) => (d.id || d._id) === oldDesignation)?.name ||
                    oldDesignation || ""
                  }
                    placeholder="Select Option..."
                    className={`${inputCls} bg-gray-50 cursor-not-allowed text-gray-500`}
                  />
                </div>
              </div>

              {/* Row 2: New Designation + Effective Date */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>New Designation</label>
                  <select value={newDesignation} onChange={(e) => setNewDesignation(e.target.value)}
                    className={inputCls}>
                    <option value="">Select Designation...</option>
                    {designations.map((d: any) => (
                      <option key={d.id || d._id} value={d.id || d._id}>
                        {d.designationName || d.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Effective Date <span className="text-red-500">*</span></label>
                  <input type="date" value={effectiveDate} onChange={(e) => setEffectiveDate(e.target.value)}
                    required className={inputCls} />
                </div>
              </div>

              {/* Row 3: Old CTC + New CTC */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Old CTC</label>
                  <input type="number" placeholder="Ex. 10000/" value={oldCtc}
                    onChange={(e) => setOldCtc(e.target.value)} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>New CTC</label>
                  <input type="number" placeholder="Ex. 9000/" value={newCtc}
                    onChange={(e) => setNewCtc(e.target.value)} className={inputCls} />
                </div>
              </div>

              {/* Row 4: Decrement Amount + Decrement % (auto-calculated) */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>
                    Decrement Amount <span className="text-[10px] text-gray-400 font-normal">(Auto-calculated)</span>
                  </label>
                  <div className="relative">
                    <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm">₹</span>
                    <input readOnly value={decrementAmount}
                      placeholder="-"
                      className={`${inputCls} pl-7 bg-gray-50 cursor-not-allowed text-gray-600`}
                    />
                  </div>
                </div>
                <div>
                  <label className={labelCls}>
                    Decrement (%) <span className="text-[10px] text-gray-400 font-normal">(Auto-calculated)</span>
                  </label>
                  <input readOnly value={decrementPct ? `${decrementPct}%` : ""}
                    placeholder="-"
                    className={`${inputCls} bg-gray-50 cursor-not-allowed text-gray-600`}
                  />
                </div>
              </div>

              {/* Remarks */}
              <div>
                <label className={labelCls}>Remarks <span className="text-red-500">*</span></label>
                <textarea
                  value={remarks} onChange={(e) => setRemarks(e.target.value)}
                  placeholder="Enter remarks..."
                  rows={3} required
                  className={`${inputCls} resize-none`}
                />
              </div>

              {/* Footer Buttons */}
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => { setShowForm(false); resetForm(); }}
                  className="px-5 py-2.5 rounded-xl border border-[#D2D2D2] text-[#212121] text-sm font-semibold hover:bg-gray-50 transition cursor-pointer">
                  Cancel
                </button>
                <button type="submit" disabled={isSubmitting}
                  className="px-6 py-2.5 rounded-xl bg-[#212121] text-white text-sm font-bold hover:bg-gray-800 transition cursor-pointer disabled:opacity-60">
                  {isSubmitting ? "Saving..." : "Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Expense Request Tab ───────────────────────────────────────────────────
function StatusBadge({ status }: { status: string }) {
  const s = (status || "").toLowerCase();
  const cfg: Record<string, { dot: string; bg: string; text: string }> = {
    pending:  { dot: "bg-amber-400",  bg: "bg-amber-50",  text: "text-amber-700" },
    approved: { dot: "bg-emerald-500",bg: "bg-emerald-50",text: "text-emerald-700" },
    rejected: { dot: "bg-red-500",    bg: "bg-red-50",    text: "text-red-700" },
    paid:     { dot: "bg-blue-500",   bg: "bg-blue-50",   text: "text-blue-700" },
  };
  const c = cfg[s] || cfg.pending;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold ${c.bg} ${c.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`} />
      {status || "Pending"}
    </span>
  );
}

function ExpenseDetailsModal({ item, onClose }: { item: any; onClose: () => void }) {
  if (!item) return null;
  const formatDate = (d: string) => {
    if (!d) return "-";
    const dt = new Date(d);
    return isNaN(dt.getTime()) ? d : dt.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  };
  const Field = ({ label, value }: { label: string; value?: string }) => (
    <div>
      <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-0.5">{label}</p>
      <p className="text-sm font-semibold text-[#212121]">{value || "-"}</p>
    </div>
  );
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden animate-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-lg font-bold text-[#212121]">Expense Details</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 transition cursor-pointer">
            <IconX size={18} />
          </button>
        </div>
        {/* Body */}
        <div className="px-6 py-5 space-y-5">
          <div className="grid grid-cols-2 gap-x-8 gap-y-4">
            <Field label="Employee" value={item.employeeName || item.employee?.name || item.vendorName || "-"} />
            <Field label="Title" value={item.title} />
            <Field label="Amount" value={item.amount != null ? `₹${Number(item.amount).toLocaleString("en-IN")}` : "-"} />
            <Field label="Expense Date" value={formatDate(item.expenseDate)} />
            <Field label="Purpose" value={item.purpose} />
            <Field label="Expense Type" value={item.expenseType} />
            <Field label="Payment Mode" value={item.paymentMode} />
            <Field label="Paid Amount" value={item.paidAmount != null ? `₹${Number(item.paidAmount).toLocaleString("en-IN")}` : "-"} />
            <Field label="Reference ID" value={item.referenceId} />
            <Field label="Status" value={item.status} />
          </div>
          {item.description && (
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-0.5">Description</p>
              <p className="text-sm text-[#212121]">{item.description}</p>
            </div>
          )}
          {/* Bill Section */}
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-2">Bill</p>
            {item.billUrl ? (
              <a href={item.billUrl} target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-blue-600 bg-blue-50 rounded-xl hover:bg-blue-100 transition">
                View Bill
              </a>
            ) : (
              <div className="w-full py-4 text-center text-sm text-red-500 font-semibold bg-red-50 rounded-xl">
                Expense Bill not found
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function ExpenseRequestTab() {
  const [data, setData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [limit] = useState(10);
  const [viewItem, setViewItem] = useState<any | null>(null);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await fetchHrExpenseRequests(currentPage, limit, search);
      if (res.success) {
        let list: any[] = [];
        const raw = res.data?.expenseRequests ?? res.data?.data ?? res.data ?? [];
        list = Array.isArray(raw) ? raw : [];
        setData(list);
        const total = res.data?.total ?? res.data?.totalCount ?? list.length;
        setTotalRecords(total);
        setTotalPages(res.data?.totalPages ?? (Math.ceil(total / limit) || 1));
      }
    } catch (err) {
      console.error("Failed to load expense requests:", err);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, limit, search]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleView = async (item: any) => {
    setOpenMenuId(null);
    try {
      const res = await fetchHrExpenseRequestById(item.id || item._id);
      setViewItem(res.success && res.data ? res.data : item);
    } catch {
      setViewItem(item);
    }
  };

  const formatDate = (d: string) => {
    if (!d) return "-";
    const dt = new Date(d);
    if (isNaN(dt.getTime())) return d;
    return dt.toLocaleDateString("en-IN", { day: "2-digit", month: "2-digit", year: "numeric" }).replace(/\//g, "-");
  };

  const formatDateTime = (d: string) => {
    if (!d) return "-";
    const dt = new Date(d);
    if (isNaN(dt.getTime())) return d;
    return (
      dt.toLocaleDateString("en-IN", { day: "2-digit", month: "2-digit", year: "numeric" }).replace(/\//g, "-") +
      "\n" +
      dt.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true })
    );
  };

  const colClass = "px-4 py-3 text-[11px] font-bold uppercase tracking-wider text-gray-500 whitespace-nowrap";
  const cellClass = "px-4 py-3 text-[13px] text-[#212121] border-b border-gray-100 whitespace-nowrap";

  return (
    <div className="space-y-4 animate-in fade-in duration-300">
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-xl px-3 py-2 shadow-sm w-64">
          <IconSearch size={15} className="text-gray-400 shrink-0" />
          <input
            type="text"
            placeholder="Search..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
            className="flex-1 text-sm bg-transparent outline-none text-[#212121] placeholder-gray-400"
          />
          {search && (
            <button onClick={() => { setSearch(""); setCurrentPage(1); }} className="text-gray-400 hover:text-gray-600 cursor-pointer">
              <IconX size={14} />
            </button>
          )}
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <span>{limit}</span>
          <span className="text-gray-400">entries</span>
          <button onClick={loadData} className="p-2 rounded-xl bg-[#FBD037] hover:bg-[#e5bc32] transition text-[#212121] shadow-sm cursor-pointer" title="Refresh">
            <IconRefresh size={15} />
          </button>
        </div>
      </div>

      {/* Table Card */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50/80 border-b border-gray-100">
              <tr>
                <th className={colClass}></th>
                <th className={colClass}>SR.NO</th>
                <th className={colClass}>Employee / Vendor</th>
                <th className={colClass}>Title</th>
                <th className={colClass}>Amount</th>
                <th className={colClass}>Purpose</th>
                <th className={colClass}>Expense Date</th>
                <th className={colClass}>Processed By</th>
                <th className={colClass}>Payment Mode</th>
                <th className={colClass}>Paid Amount</th>
                <th className={colClass}>Status</th>
                <th className={colClass}>Created At</th>
                <th className={colClass}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr><td colSpan={13} className="text-center py-12 text-gray-400 text-sm">Loading...</td></tr>
              ) : data.length === 0 ? (
                <tr><td colSpan={13} className="text-center py-12 text-gray-400 text-sm">No expense requests found.</td></tr>
              ) : data.map((item: any, idx: number) => {
                const name = item.employeeName || item.employee?.name || item.vendorName || "-";
                const initials = name !== "-" ? name.split(" ").map((n: string) => n[0]).join("").slice(0, 2).toUpperCase() : "?";
                const designation = item.designation || item.employee?.designation || item.department || "";
                const processedName = item.processedByName || item.processedBy?.name || "-";
                const processedDept = item.processedByDepartment || item.processedBy?.department || "";
                const processedInitial = processedName !== "-" ? processedName[0]?.toUpperCase() : "?";
                const srNo = (currentPage - 1) * limit + idx + 1;
                const itemId = item.id || item._id;
                const [datePart, timePart] = formatDateTime(item.createdAt).split("\n");
                return (
                  <tr key={itemId} className="hover:bg-gray-50/60 transition-colors group">
                    {/* Checkbox placeholder */}
                    <td className={cellClass}>
                      <input type="checkbox" className="w-3.5 h-3.5 rounded border-gray-300 accent-[#FBD037] cursor-pointer" />
                    </td>
                    {/* SR NO */}
                    <td className={`${cellClass} font-semibold text-gray-400`}>{srNo}</td>
                    {/* Employee / Vendor */}
                    <td className={cellClass}>
                      <div className="flex items-center gap-2.5">
                        <span className="w-8 h-8 rounded-full bg-[#FBD037]/30 border border-[#FBD037]/50 flex items-center justify-center text-xs font-bold text-[#212121] shrink-0">
                          {initials}
                        </span>
                        <div>
                          <div className="font-semibold text-[#212121] text-[13px]">{name}</div>
                          {designation && <div className="text-[11px] text-gray-400">{designation}</div>}
                        </div>
                      </div>
                    </td>
                    {/* Title */}
                    <td className={cellClass}>{item.title || "-"}</td>
                    {/* Amount */}
                    <td className={cellClass}>
                      <span className="flex items-center gap-1">
                        {item.amount != null ? `₹${Number(item.amount).toLocaleString("en-IN")}` : "-"}
                        <IconInfoCircle size={13} className="text-gray-300" />
                      </span>
                    </td>
                    {/* Purpose */}
                    <td className={cellClass}>{item.purpose || "-"}</td>
                    {/* Expense Date */}
                    <td className={cellClass}>{formatDate(item.expenseDate)}</td>
                    {/* Processed By */}
                    <td className={cellClass}>
                      {processedName !== "-" ? (
                        <div className="flex items-center gap-2">
                          <span className="w-7 h-7 rounded-full bg-[#FBD037]/20 border border-[#FBD037]/40 flex items-center justify-center text-[11px] font-bold text-[#212121] shrink-0">
                            {processedInitial}
                          </span>
                          <div>
                            <div className="text-[13px] font-semibold text-[#212121]">{processedName}</div>
                            {processedDept && <div className="text-[11px] text-gray-400">{processedDept}</div>}
                          </div>
                        </div>
                      ) : <span className="text-gray-400">-</span>}
                    </td>
                    {/* Payment Mode */}
                    <td className={cellClass}>{item.paymentMode || item.paymentmode || "-"}</td>
                    {/* Paid Amount */}
                    <td className={cellClass}>{item.paidAmount != null ? `₹ ${Number(item.paidAmount).toLocaleString("en-IN")}` : "-"}</td>
                    {/* Status */}
                    <td className={cellClass}><StatusBadge status={item.status} /></td>
                    {/* Created At */}
                    <td className={cellClass}>
                      <div className="text-[12px] text-[#212121]">{datePart}</div>
                      <div className="text-[11px] text-gray-400">{timePart}</div>
                    </td>
                    {/* Actions */}
                    <td className={`${cellClass} relative`}>
                      <button
                        onClick={() => setOpenMenuId(openMenuId === itemId ? null : itemId)}
                        className="p-1.5 rounded-lg hover:bg-gray-100 transition cursor-pointer"
                      >
                        <IconDotsVertical size={16} className="text-gray-400" />
                      </button>
                      {openMenuId === itemId && (
                        <div className="absolute right-6 top-8 z-20 bg-white border border-gray-100 shadow-xl rounded-xl w-40 py-1 animate-in fade-in zoom-in-95 duration-150">
                          <button
                            onClick={() => handleView(item)}
                            className="flex items-center gap-2 w-full px-4 py-2.5 text-[13px] text-[#212121] hover:bg-gray-50 transition cursor-pointer"
                          >
                            <IconEye size={14} /> View Details
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 text-sm text-gray-500">
            <span>Record {(currentPage - 1) * limit + 1}–{Math.min(currentPage * limit, totalRecords)} of {totalRecords}</span>
            <div className="flex items-center gap-1">
              <button
                disabled={currentPage <= 1}
                onClick={() => setCurrentPage(p => p - 1)}
                className="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-40 transition cursor-pointer"
              >
                <IconChevronLeft size={15} />
              </button>
              {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => i + 1).map(pg => (
                <button
                  key={pg}
                  onClick={() => setCurrentPage(pg)}
                  className={`w-8 h-8 rounded-lg text-xs font-semibold transition cursor-pointer ${
                    pg === currentPage ? "bg-[#FBD037] text-[#212121] shadow-sm" : "border border-gray-200 hover:bg-gray-50 text-gray-500"
                  }`}
                >
                  {pg}
                </button>
              ))}
              <button
                disabled={currentPage >= totalPages}
                onClick={() => setCurrentPage(p => p + 1)}
                className="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-40 transition cursor-pointer"
              >
                <IconChevronRight size={15} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Expense Details Modal */}
      {viewItem && <ExpenseDetailsModal item={viewItem} onClose={() => setViewItem(null)} />}

      {/* Close dropdown on outside click */}
      {openMenuId && (
        <div className="fixed inset-0 z-10" onClick={() => setOpenMenuId(null)} />
      )}
    </div>
  );
}

export default function ActionsTab() {
  const [activeSubTab, setActiveSubTab] = useState("Warning");
  const subTabs = ["Warning", "Fine", "Notice Period", "Relegation", "Expense Request"];
  const [companyId, setCompanyId] = useState("");
  const [branchId, setBranchId] = useState("");
  const [isLoadingMeta, setIsLoadingMeta] = useState(false);

  useEffect(() => {
    const loadMetadata = async () => {
      setIsLoadingMeta(true);
      try {
        const compRes = await fetchHrCompany();
        const branchRes = await fetchHrBranches(1, 100);
        const cid = compRes.success && (compRes.data?.id || compRes.data?._id || compRes.data?.company?.id);
        const bid = branchRes.success && branchRes.data?.branches?.[0]?.id;
        if (cid) setCompanyId(cid);
        if (bid) setBranchId(bid);
      } catch (err) {
        console.error("Error loading company/branch metadata:", err);
      } finally {
        setIsLoadingMeta(false);
      }
    };
    loadMetadata();
  }, []);

  return (
    <div className="p-0 mt-4">
      {/* Sub-tabs */}
      <div className="flex justify-center pb-4">
        <div className="whitespace-nowrap overflow-x-auto no-scrollbar">
          <RoleToggle
            options={subTabs}
            active={activeSubTab}
            onChange={setActiveSubTab}
          />
        </div>
      </div>

      {/* Sub-tab Content */}
      <div>
        {activeSubTab === "Warning" && (
          <WarningTab companyId={companyId} branchId={branchId} isLoadingMeta={isLoadingMeta} />
        )}
        {activeSubTab === "Fine" && (
          <FineTab companyId={companyId} branchId={branchId} isLoadingMeta={isLoadingMeta} />
        )}
        {activeSubTab === "Notice Period" && (
          <NoticePeriodTab companyId={companyId} branchId={branchId} isLoadingMeta={isLoadingMeta} />
        )}
        {activeSubTab === "Relegation" && (
          <RelegationTab />
        )}
        {activeSubTab === "Expense Request" && (
          <ExpenseRequestTab />
        )}
      </div>
    </div>
  );
}
